import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiFetch } from '@/lib/api';
import { approveRequestResponseSchema, type LicenseRequest } from '@/schemas/license-requests';
import { RequestStatusWord, OfflineFlag, submitterLabel } from './requests-queue-table';

function fmtTs(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : format(d, 'yyyy-MM-dd HH:mm:ss');
}

function conflictToast(err: unknown, t: (k: string) => string) {
  if (err instanceof ApiError && err.status === 409) {
    // Server distinguishes double-transition from serial-already-licensed.
    const alreadyLicensed = /already exists/i.test(err.message);
    toast.error(
      t(alreadyLicensed ? 'requests.toasts.alreadyLicensed' : 'requests.toasts.alreadyReviewed')
    );
  } else {
    toast.error(t('requests.toasts.error'));
  }
}

interface ApproveDialogProps {
  request: LicenseRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproved: (request: LicenseRequest) => void;
}

function ApproveDialog({ request, open, onOpenChange, onApproved }: ApproveDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const resp = await apiFetch(`/license-requests/${request.id}/approve`, { method: 'POST' });
      return approveRequestResponseSchema.parse(await resp.json());
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['license-requests'] });
      onOpenChange(false);
      onApproved(data.request);
      toast.success(t('requests.toasts.approvedPending'), {
        description: data.license.serial,
        action: {
          label: t('requests.toasts.openLicense'),
          onClick: () => navigate(`/licenses/${data.license.id}`),
        },
      });
    },
    onError: (err) => conflictToast(err, t),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('requests.review.approveTitle')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <p>{t('requests.review.approveBody')}</p>
              <p className="font-mono text-xs">{request.serial}</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('requests.review.approveConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RejectDialogProps {
  request: LicenseRequest;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRejected: (request: LicenseRequest) => void;
}

function RejectDialog({ request, open, onOpenChange, onRejected }: RejectDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: async (reasonText: string) => {
      const resp = await apiFetch(`/license-requests/${request.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: reasonText }),
      });
      const json = (await resp.json()) as { request: LicenseRequest };
      return json.request;
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['license-requests'] });
      setReason('');
      onOpenChange(false);
      onRejected(updated);
      toast.success(t('requests.toasts.rejected'));
    },
    onError: (err) => conflictToast(err, t),
  });

  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setReason('');
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('requests.review.rejectTitle')}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-1">
              <p>{t('requests.review.rejectBody')}</p>
              <p className="font-mono text-xs">{request.serial}</p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reject-reason">{t('requests.review.reasonLabel')}</Label>
          <textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={512}
            rows={3}
            required
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            aria-describedby="reject-reason-hint"
          />
          <div className="flex justify-between">
            <p id="reject-reason-hint" className="text-xs text-muted-foreground">
              {t('requests.review.reasonHint')}
            </p>
            <span className="text-xs text-muted-foreground">{reason.length}/512</span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate(reason.trim())}
            disabled={!canSubmit}
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('requests.review.rejectConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="break-all text-[13.5px] text-foreground">{children}</span>
    </div>
  );
}

interface RequestReviewSheetProps {
  request: LicenseRequest | null;
  open: boolean;
  onClose: () => void;
}

export function RequestReviewSheet({ request, open, onClose }: RequestReviewSheetProps) {
  const { t } = useTranslation();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  if (!request) return null;

  const cfg = request.configuration;
  const isPending = request.status === 'submitted';

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
        <SheetContent className="w-[480px] max-w-full sm:max-w-[480px]">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <span className="truncate font-mono text-base">{request.serial}</span>
            </SheetTitle>
            <div className="flex items-center gap-3">
              <RequestStatusWord status={request.status} />
              <OfflineFlag request={request} />
            </div>
          </SheetHeader>

          <ScrollArea className="mt-4 h-[calc(100vh-150px)] pr-2">
            <div className="space-y-4">
              <div className="space-y-3">
                <InfoRow label={t('requests.queue.buyer')}>{request.buyerCompany}</InfoRow>
                <InfoRow label={t('requests.queue.submitter')}>
                  <span className="font-mono text-[12.5px]">{submitterLabel(request, t)}</span>
                </InfoRow>
                <InfoRow label={t('requests.queue.created')}>{fmtTs(request.createdAt)}</InfoRow>
              </div>

              <Separator />

              <div>
                <h4 className="mb-2 text-sm font-semibold">{t('requests.review.configuration')}</h4>
                <div className="space-y-3">
                  <InfoRow label={t('requests.review.type')}>
                    {cfg.type ? t(`common.type.${cfg.type}`, cfg.type) : '—'}
                  </InfoRow>
                  <InfoRow label={t('requests.review.maxActivations')}>
                    <span className="font-mono tabular-nums">{cfg.maxActivations ?? '—'}</span>
                  </InfoRow>
                  <InfoRow label={t('requests.review.expiresAt')}>
                    {cfg.expiresAt ? fmtTs(cfg.expiresAt) : t('common.never')}
                  </InfoRow>
                  {cfg.entitlements && cfg.entitlements.length > 0 && (
                    <InfoRow label={t('requests.review.entitlements')}>
                      <span className="font-mono text-[12.5px]">
                        {cfg.entitlements.map((e) => `${e.module}:${e.tier}`).join(', ')}
                      </span>
                    </InfoRow>
                  )}
                </div>
              </div>

              {!isPending && (
                <>
                  <Separator />
                  <div>
                    <h4 className="mb-2 text-sm font-semibold">{t('requests.review.outcome')}</h4>
                    <div className="space-y-3">
                      <InfoRow label={t('requests.review.reviewedBy')}>
                        <span className="font-mono text-[12.5px]">{request.reviewedBy ?? '—'}</span>
                      </InfoRow>
                      <InfoRow label={t('requests.review.reviewedAt')}>
                        {fmtTs(request.reviewedAt)}
                      </InfoRow>
                      {request.status === 'rejected' && request.rejectionReason && (
                        <InfoRow label={t('requests.review.rejectionReason')}>
                          {request.rejectionReason}
                        </InfoRow>
                      )}
                      {request.status === 'approved' && request.resultingLicenseId && (
                        <InfoRow label={t('requests.review.resultingLicense')}>
                          <Link
                            to={`/licenses/${request.resultingLicenseId}`}
                            className="font-mono text-[12.5px] text-primary underline-offset-4 hover:underline"
                          >
                            {request.resultingLicenseId}
                          </Link>
                        </InfoRow>
                      )}
                    </div>
                  </div>
                </>
              )}

              {isPending && (
                <>
                  <Separator />
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Button className="flex-1" onClick={() => setApproveOpen(true)}>
                      {t('requests.review.approve')}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setRejectOpen(true)}
                    >
                      {t('requests.review.reject')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {isPending && (
        <>
          <ApproveDialog
            request={request}
            open={approveOpen}
            onOpenChange={setApproveOpen}
            onApproved={() => onClose()}
          />
          <RejectDialog
            request={request}
            open={rejectOpen}
            onOpenChange={setRejectOpen}
            onRejected={() => onClose()}
          />
        </>
      )}
    </>
  );
}
